import { useState } from 'react';
import Card from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import { useQuery } from '@tanstack/react-query';
import { authenticatedFetch } from '@/utils/authenticatedFetch';

interface Document {
  id: string;
  title: string;
  description: string;
  documentType: string;
  category: string;
  status: string;
  confidentiality: string;
  createdAt: string;
  expiryDate?: string;
  fileSize: number;
}

interface DocumentSummary {
  totalDocuments: number;
  documentsByType: Record<string, number>;
  documentsByStatus: Record<string, number>;
  expiringSoon: number;
  expired: number;
  totalFileSize: number;
}

export default function DocumentVaultPage() {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');

  const { data: documents = [] as Document[], isLoading: documentsLoading, error: documentsError } = useQuery<Document[]>({
    queryKey: ['documents', searchTerm, selectedType, selectedStatus],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (selectedType !== 'all') params.append('documentType', selectedType);
      if (selectedStatus !== 'all') params.append('status', selectedStatus);
      
      const response = await authenticatedFetch(`/api/document-vault?${params}`);
      if (!response.ok) throw new Error('Failed to fetch documents');
      const result = await response.json();
      return result.data;
    }
  });

  const { data: summary = {} as DocumentSummary, isLoading: summaryLoading } = useQuery<DocumentSummary>({
    queryKey: ['document-summary'],
    queryFn: async () => {
      const response = await authenticatedFetch('/api/document-vault/summary/overview');
      if (!response.ok) throw new Error('Failed to fetch summary');
      const result = await response.json();
      return result.data;
    }
  });

  const { data: expiringDocuments = [] as Document[] } = useQuery<Document[]>({
    queryKey: ['expiring-documents'],
    queryFn: async () => {
      const response = await authenticatedFetch('/api/document-vault/expiring/list?daysAhead=30');
      if (!response.ok) throw new Error('Failed to fetch expiring documents');
      const result = await response.json();
      return result.data;
    }
  });

  const handleUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('document', file);
    formData.append('title', file.name);
    formData.append('description', '');
    formData.append('documentType', 'other');
    formData.append('category', 'general');
    formData.append('priority', 'medium');
    formData.append('confidentiality', 'internal');

    try {
      const response = await authenticatedFetch('/api/document-vault/upload', {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) throw new Error('Failed to upload document');
      
      // Refresh documents list
      window.location.reload();
    } catch (error) {
      console.error('Upload error:', error);
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  if (documentsLoading || summaryLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (documentsError || !documents) {
    return (
      <div className="text-center py-8">
        <p className="text-red-600">Failed to load documents</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Document Vault</h1>
          <p className="text-gray-600">Secure document management and version control</p>
        </div>
        <div className="flex space-x-2">
          <input
            type="file"
            id="document-upload"
            className="hidden"
            onChange={handleUpload}
            accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.txt"
          />
          <Button
            onClick={() => document.getElementById('document-upload')?.click()}
            className="bg-blue-600 hover:bg-blue-700"
          >
            Upload Document
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <Card.Header className="pb-2">
              <Card.Title className="text-sm font-medium text-gray-600">Total Documents</Card.Title>
            </Card.Header>
            <Card.Content>
              <div className="text-2xl font-bold">{summary.totalDocuments}</div>
            </Card.Content>
          </Card>
          
          <Card>
            <Card.Header className="pb-2">
              <Card.Title className="text-sm font-medium text-gray-600">Active Documents</Card.Title>
            </Card.Header>
            <Card.Content>
              <div className="text-2xl font-bold text-green-600">
                {summary.documentsByStatus.active || 0}
              </div>
            </Card.Content>
          </Card>
          
          <Card>
            <Card.Header className="pb-2">
              <Card.Title className="text-sm font-medium text-gray-600">Expiring Soon</Card.Title>
            </Card.Header>
            <Card.Content>
              <div className="text-2xl font-bold text-yellow-600">{summary.expiringSoon}</div>
            </Card.Content>
          </Card>
          
          <Card>
            <Card.Header className="pb-2">
              <Card.Title className="text-sm font-medium text-gray-600">Storage Used</Card.Title>
            </Card.Header>
            <Card.Content>
              <div className="text-2xl font-bold">{formatFileSize(summary.totalFileSize)}</div>
            </Card.Content>
          </Card>
        </div>
      )}

      {/* Expiring Documents Alert */}
      {expiringDocuments && expiringDocuments.length > 0 && (
        <Card className="border-yellow-200 bg-yellow-50">
          <Card.Header>
            <Card.Title className="text-yellow-800">Documents Expiring Soon</Card.Title>
          </Card.Header>
          <Card.Content>
            <div className="space-y-2">
              {expiringDocuments.slice(0, 3).map((doc) => (
                <div key={doc.id} className="flex justify-between items-center">
                  <span className="text-sm">{doc.title}</span>
                  <span className="text-xs text-yellow-600">
                    {doc.expiryDate ? new Date(doc.expiryDate).toLocaleDateString() : 'No expiry date'}
                  </span>
                </div>
              ))}
            </div>
          </Card.Content>
        </Card>
      )}

      {/* Filters and Search */}
      <Card>
        <Card.Header>
          <Card.Title>Filters</Card.Title>
        </Card.Header>
        <Card.Content>
          <div className="flex flex-col md:flex-row gap-4">
            <Input
              placeholder="Search documents..."
              value={searchTerm}
              onChange={(e: any) => setSearchTerm(e.target.value)}
              className="flex-1"
            />
            
            <select
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Types</option>
              <option value="contract">Contracts</option>
              <option value="license">Licenses</option>
              <option value="permit">Permits</option>
              <option value="certificate">Certificates</option>
              <option value="agreement">Agreements</option>
              <option value="policy">Policies</option>
              <option value="report">Reports</option>
              <option value="other">Other</option>
            </select>
            
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Status</option>
              <option value="draft">Draft</option>
              <option value="active">Active</option>
              <option value="expired">Expired</option>
              <option value="revoked">Revoked</option>
              <option value="archived">Archived</option>
            </select>
          </div>
        </Card.Content>
      </Card>

      {/* Documents List */}
      <Card>
        <Card.Header>
          <Card.Title>Documents ({documents.length})</Card.Title>
        </Card.Header>
        <Card.Content>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Title
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Size
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {documents.map((doc: any) => (
                  <tr key={doc.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{doc.title}</div>
                        <div className="text-sm text-gray-500">{doc.description}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                        {doc.documentType}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        doc.status === 'active' 
                          ? 'bg-green-100 text-green-800'
                          : doc.status === 'expired'
                          ? 'bg-red-100 text-red-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {doc.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatFileSize(doc.fileSize)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(doc.createdAt).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => window.open(`/api/document-vault/${doc.id}/download`, '_blank')}
                        >
                          Download
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => window.open(`/api/document-vault/${doc.id}`, '_blank')}
                        >
                          View
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            
            {documents.length === 0 && (
              <div className="text-center py-8">
                <p className="text-gray-500">No documents found</p>
              </div>
            )}
          </div>
        </Card.Content>
      </Card>
    </div>
  );
}
